import * as React from 'react';

import { COMPONENT_IDS } from '@/hen-ui/catalog/component-ids';
import { cn } from '@/hen-ui/utils';
import { tagLinkVariants, type TagLinkVariantProps } from './tag-link.variants';

function hasNavigableHref(href: string | undefined): href is string {
  return href != null && href !== '' && href !== '#';
}

/** Space keydowns waiting for keyup, so release can't activate a new focus target. */
const armedForSpace = new WeakMap<Element, () => void>();

function armSpace(target: Element): void {
  if (armedForSpace.has(target)) return;
  const disarmOnBlur = () => disarmSpace(target);
  target.addEventListener('blur', disarmOnBlur);
  armedForSpace.set(target, () => {
    target.removeEventListener('blur', disarmOnBlur);
    armedForSpace.delete(target);
  });
}

function disarmSpace(target: Element): void {
  armedForSpace.get(target)?.();
}

export interface TagLinkProps
  extends TagLinkVariantProps,
    Omit<
      React.AnchorHTMLAttributes<HTMLAnchorElement> &
        React.ButtonHTMLAttributes<HTMLButtonElement>,
      'href' | 'type' | 'size' | 'children'
    > {
  /**
   * Tag text — catalog authors set a string. Leading icons use `iconBefore`.
   */
  children?: string;
  /**
   * Optional leading icon. Catalog authors drop `hen.icon` into this slot.
   * Mark it decorative so the Tag Link text stays the accessible name.
   */
  iconBefore?: React.ReactNode;
  /**
   * Navigable web address. Empty or `#` means no navigation — use a click
   * action (for example Open modal) instead; the host is then a button.
   */
  href?: string;
  /**
   * When true, the tag shows a deeper tint of its color on hover and press.
   * Off by default: only the pointer cursor signals it is clickable.
   */
  darkenOnHover?: boolean;
  /** When true, opens a navigable web address in a new browser tab. */
  openInNewTab?: boolean;
}

/**
 * Tag Link — compact clickable tag that navigates or runs an action.
 *
 * Renders a real `<a>` when `href` is a navigable web address, and
 * `<button type="button">` for click actions such as Open modal. With
 * neither, it renders a `<span>` so screen readers never hear a button
 * that does nothing.
 */
export const TagLink = React.forwardRef<
  HTMLAnchorElement | HTMLButtonElement | HTMLSpanElement,
  TagLinkProps
>(
  (
    {
      className,
      variant,
      size,
      darkenOnHover,
      children,
      iconBefore,
      href,
      openInNewTab,
      disabled,
      onClick,
      onKeyDown,
      onKeyUp,
      target,
      rel,
      ...props
    },
    ref,
  ) => {
    const content =
      iconBefore == null ? (
        children
      ) : (
        <>
          {iconBefore}
          {children}
        </>
      );
    const classNames = cn(
      tagLinkVariants({ variant, size, darkenOnHover }),
      className,
    );
    const asLink = hasNavigableHref(href);

    if (asLink) {
      const opensInNewTab =
        openInNewTab === true || (openInNewTab == null && target === '_blank');
      const resolvedTarget = opensInNewTab
        ? '_blank'
        : openInNewTab === false
          ? undefined
          : target;
      const resolvedRel = opensInNewTab
        ? rel || 'noopener noreferrer'
        : openInNewTab === false
          ? undefined
          : rel;
      const { type: _ignoredButtonType, ...anchorRest } =
        props as React.AnchorHTMLAttributes<HTMLAnchorElement> & {
          type?: string;
        };

      return (
        <a
          ref={ref as React.Ref<HTMLAnchorElement>}
          {...anchorRest}
          data-hen-id={COMPONENT_IDS.TagLink}
          className={classNames}
          href={href}
          target={resolvedTarget}
          rel={resolvedRel}
          aria-disabled={disabled || undefined}
          tabIndex={disabled ? -1 : anchorRest.tabIndex}
          onKeyDown={
            onKeyDown as
              | React.KeyboardEventHandler<HTMLAnchorElement>
              | undefined
          }
          onKeyUp={
            onKeyUp as React.KeyboardEventHandler<HTMLAnchorElement> | undefined
          }
          onClick={(event) => {
            if (disabled) {
              event.preventDefault();
              return;
            }
            (
              onClick as React.MouseEventHandler<HTMLAnchorElement> | undefined
            )?.(event);
          }}
        >
          {content}
        </a>
      );
    }

    if (onClick == null) {
      const { type: _ignoredButtonType, ...spanRest } =
        props as React.HTMLAttributes<HTMLSpanElement> & { type?: string };

      return (
        <span
          ref={ref as React.Ref<HTMLSpanElement>}
          {...spanRest}
          data-hen-id={COMPONENT_IDS.TagLink}
          className={cn(
            tagLinkVariants({ variant, size, interactive: false }),
            className,
          )}
          aria-disabled={disabled || undefined}
          onKeyDown={
            onKeyDown as React.KeyboardEventHandler<HTMLSpanElement> | undefined
          }
          onKeyUp={
            onKeyUp as React.KeyboardEventHandler<HTMLSpanElement> | undefined
          }
        >
          {content}
        </span>
      );
    }

    return (
      <button
        ref={ref as React.Ref<HTMLButtonElement>}
        {...(props as React.ButtonHTMLAttributes<HTMLButtonElement>)}
        type='button'
        data-hen-id={COMPONENT_IDS.TagLink}
        className={classNames}
        disabled={disabled}
        onClick={onClick as React.MouseEventHandler<HTMLButtonElement>}
        onKeyDown={(event) => {
          (
            onKeyDown as
              | React.KeyboardEventHandler<HTMLButtonElement>
              | undefined
          )?.(event);
          if (event.defaultPrevented || event.target !== event.currentTarget) {
            if (event.key === ' ') disarmSpace(event.currentTarget);
            return;
          }
          if (event.key === ' ') {
            event.preventDefault();
            armSpace(event.currentTarget);
          }
        }}
        onKeyUp={(event) => {
          (
            onKeyUp as React.KeyboardEventHandler<HTMLButtonElement> | undefined
          )?.(event);
          if (event.key !== ' ') return;
          const armed = armedForSpace.has(event.currentTarget);
          disarmSpace(event.currentTarget);
          if (
            !armed ||
            event.defaultPrevented ||
            disabled ||
            event.target !== event.currentTarget
          ) {
            return;
          }
          event.currentTarget.click();
        }}
      >
        {content}
      </button>
    );
  },
);

TagLink.displayName = 'TagLink';
